import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';

export type WalletPlatform = 'android' | 'ios';

export interface WalletVersoBlock {
  id?: string;
  type?: string;
  label?: string;
  content: string;
}

// Données affichées sur la carte : celles propres au porteur sont des exemples, comme sur l'aperçu BeOne
export interface WalletCard {
  company: string;
  logo: string;
  programName: string;
  rectoImage?: string;
  title: string;
  message: string;
  blocks: WalletVersoBlock[];
}

export const SAMPLE = {
  time: '10:47',
  firstname: 'PRENOM',
  lastname: 'NOM',
  points: '0',
  label: 'LABEL1',
  value: 'value1',
  cardNumber: '3660298000044',
  store: 'Lille',
};

const FONT = {
  android: 'Roboto, "Segoe UI", sans-serif',
  ios: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif',
};

// Maquette de téléphone : couleurs littérales, hors thème
export function PhoneFrame({ platform, screenBg, children }: { platform: WalletPlatform; screenBg: string; children: ReactNode }) {
  return (
    <Box sx={{ width: 240, flexShrink: 0, bgcolor: '#000', borderRadius: '36px', p: '12px', pt: '26px', pb: '26px', position: 'relative', boxShadow: '0 6px 18px rgba(0,0,0,0.25)' }}>
      <Box
        sx={{
          position: 'absolute', top: 9, left: '50%', transform: 'translateX(-50%)', bgcolor: '#fff',
          ...(platform === 'ios' ? { width: 34, height: 7, borderRadius: 4 } : { width: 7, height: 7, borderRadius: '50%' }),
        }}
      />
      <Box sx={{ height: 440, bgcolor: screenBg, borderRadius: '6px', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: FONT[platform], '& .MuiTypography-root': { fontFamily: FONT[platform] } }}>
        {children}
      </Box>
    </Box>
  );
}

export function StatusBar({ color }: { color: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, pt: 0.75, color }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>{SAMPLE.time}</Typography>
      <BatteryFullIcon sx={{ fontSize: 12, transform: 'rotate(90deg)' }} />
    </Box>
  );
}

export function Logo({ src, size }: { src: string; size: number }) {
  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
    />
  );
}

// Code-barres décoratif : motif fixe dérivé du numéro de carte
export function Barcode({ value, height = 44 }: { value: string; height?: number }) {
  const widths = Array.from(`${value}${value}`).map(digit => (Number(digit) % 3) + 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', height, gap: '1px' }}>
      {widths.map((width, index) => (
        <Box key={index} sx={{ width: width * 1.2, bgcolor: index % 2 === 0 ? '#000' : 'transparent' }} />
      ))}
    </Box>
  );
}

export function Field({ label, value, align = 'left' }: { label: string; value: string; align?: 'left' | 'right' }) {
  return (
    <Box sx={{ textAlign: align }}>
      <Typography sx={{ fontSize: '0.45rem', fontWeight: 700, letterSpacing: 0.3, color: '#444' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>{value}</Typography>
    </Box>
  );
}

export function RectoImage({ card, height }: { card: WalletCard; height: number }) {
  const src = card.rectoImage || card.logo;
  if (!src) return <Box sx={{ height, bgcolor: '#f1f3f5' }} />;
  return <Box component="img" src={src} alt="" sx={{ width: '100%', height, objectFit: card.rectoImage ? 'cover' : 'contain', display: 'block' }} />;
}
